import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import Loader from 'components/Loader/Loader';
import Thumbnail from 'components/Thumbnails/Thumbnail';
import { showActionToast } from 'components/ActionToast';
import { loadProjectStream } from 'redux/modules/portfolio';
import { getAudioSources, getVideoSources } from 'helpers/projectMedia';
import { getThumbnailByWidth } from 'helpers/HelperFunctions';
import styles from './PortfolioProjects.module.scss';

/**
 * Plays a video or audio project inside its card. Only mounted once someone
 * presses play: the stream is fetched then, and the browser's own player is
 * used so no player library loads with the page.
 */
const PortfolioProjectPlayer = ({ project, onClose }) => {
  const dispatch = useDispatch();
  const [sources, setSources] = useState(null);
  const isVideo = project.mediaType === 'video';

  useEffect(() => {
    let cancelled = false;

    dispatch(loadProjectStream(project.hash))
      .then((result) => {
        if (cancelled) return;
        const found = isVideo
          ? getVideoSources(result?.stream)
          : getAudioSources(result?.stream);
        if (found.length === 0) {
          throw new Error('No playable sources');
        }
        setSources(found);
      })
      .catch(() => {
        if (cancelled) return;
        showActionToast({
          message: "We couldn't play that project. Try opening it instead.",
        });
        onClose();
      });

    return () => {
      cancelled = true;
    };
  }, [project.hash]);

  const sourceTags =
    sources &&
    sources.map((source) => (
      <source key={source.src} src={source.src} type={source.type} />
    ));

  return (
    <div className={styles.player} data-card-player="">
      {!isVideo && (
        <Thumbnail
          aspectRatio="16:9"
          thumbnails={project.thumbnails}
          style={{ width: '100%', height: '100%' }}
        />
      )}
      {!sources && (
        <div className={styles.playerLoading}>
          <Loader loading />
        </div>
      )}
      {sources && isVideo && (
        <video
          className={styles.playerVideo}
          controls
          autoPlay
          playsInline
          poster={
            project.thumbnails?.images
              ? getThumbnailByWidth(project.thumbnails.images, 500)
              : undefined
          }
          aria-label={project.title}
        >
          {sourceTags}
        </video>
      )}
      {sources && !isVideo && (
        <audio
          className={styles.playerAudio}
          controls
          autoPlay
          aria-label={project.title}
        >
          {sourceTags}
        </audio>
      )}
    </div>
  );
};

PortfolioProjectPlayer.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PortfolioProjectPlayer;
